
import { defineComponent, onBeforeMount, reactive } from 'vue'

import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import List from '@/common/components/UI/List/index.vue'
import OneCDeliveriesListItem from '@/modules/OneCModule/components/OneCDeliveriesListItem/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'

import { onec } from '@/core/realisations/onec.realisation'
import couriersAPI from '@/new-services/couriers.service'
import dispatcherSectorsAPI from '@/new-services/dispatcher-sectors.service'

import { DataState } from '@/core/classes/DataState.class'

import { OneCDelivery } from '@/core/types/one_c.types'
import useNotifications from '@/common/composable/useNotifications'
import { CompanyT } from '@/core/types/companies.types'
import { companies } from '@/core/realisations/companies.realisation'
import { Courier } from '@/new-services/types/couriers'
import { DispatcherSector } from '@/new-services/types/dispatcher-sectors'

export default defineComponent({
    components: {
        TheInput,
        TheButton,
        TheSelect,
        'one-c-deliveries-list-item': OneCDeliveriesListItem,
        FailedFetch,
        list: List,
    },
    setup() {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            option_id: 1,
            //
            ids: [null] as (number | null)[],
            //
            dispatcherSectorIds: [] as number[],
            courierIds: [] as number[],
            companyIds: [] as number[],
            invoiceNumber: null as string | null,
            address: null as string | null,
            createdAtFrom: null as string | null,
            createdAtTo: null as string | null,
        })

        const data_state = reactive({
            one_c: new DataState<OneCDelivery>({}),
            companies: new DataState<CompanyT>({}),
            couriers: new DataState<Courier>({}),
            dispatcher_sectors: new DataState<DispatcherSector>({}),
        })

        const getPayload = () => {
            return {
                dispatcherSectorIds: state.dispatcherSectorIds,
                courierIds: state.courierIds,
                companyIds: state.companyIds,
                invoiceNumber: state.invoiceNumber,
                address: state.address,
                createdAtFrom: state.createdAtFrom,
                createdAtTo: state.createdAtTo,
            }
        }

        const loadOneC = async (page: number) => {
            data_state.one_c.pagination.current_page = page
            const params = {
                page,
                dispatcherSectorIds: state.dispatcherSectorIds,
                courierIds: state.courierIds,
                companyIds: state.companyIds,
                invoiceNumber: state.invoiceNumber,
                address: state.address,
                createdAtFrom: state.createdAtFrom,
                createdAtTo: state.createdAtTo,
            }

            try {
                data_state.one_c.is_loading = true
                const response = await onec.getDeliveryList(params)

                data_state.one_c.list = response.data
                data_state.one_c.pagination.total_pages = response.meta.last_page

                data_state.one_c.is_failed = false
                data_state.one_c.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.one_c.failure.catchError(error)

                if (!data_state.one_c.failure.error_state.is_cancel) {
                    data_state.one_c.is_failed = true
                }
            } finally {
                if (!data_state.one_c.failure.error_state.is_cancel) {
                    data_state.one_c.is_loading = false
                }
            }
        }

        const reset = () => {
            state.dispatcherSectorIds = []
            state.courierIds = []
            state.companyIds = []
            state.invoiceNumber = null
            state.address = null
            state.createdAtFrom = null
            state.createdAtTo = null

            state.ids = [null]

            loadOneC(1)
        }

        const resend = async () => {
            try {
                state.is_loading = true
                await onec.resendDelivery(getPayload())
                notifications.notify({ title: 'Заказы успешно прокинуты!', type: 'success' })
                reset()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const loadCompanies = async () => {
            try {
                data_state.companies.is_loading = true

                const response = await companies.getAllList({
                    page: 1,
                    getAll: 1,
                    isExtended: 0,
                    limit: 9999,
                })
                data_state.companies.list = response.data

                data_state.companies.is_failed = false
            } catch (error) {
                data_state.companies.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка произошла ошибка',
                })
            } finally {
                data_state.companies.is_loading = false
            }
        }

        // Курьеры

        const loadCouriers = async (page: number, name?: string) => {
            data_state.couriers.pagination.current_page = page

            try {
                data_state.couriers.is_loading = true

                const params = {
                    page,
                    limit: 9999,
                    statusIds: [2],
                    name,
                }

                const response = await couriersAPI.getCouriers(params)
                data_state.couriers.list = response.data.data

                data_state.couriers.pagination.total_pages = response.data.meta.last_page

                data_state.couriers.is_failed = false
            } catch (error) {
                data_state.couriers.is_failed = true
                console.error(error)
            } finally {
                data_state.couriers.is_loading = false
            }
        }

        const searchCouriers = (name: string) => {
            loadCouriers(1, name)
        }

        const unshiftFindedCourier = async (courier: Courier) => {
            await loadCouriers(data_state.couriers.pagination.current_page)
            data_state.couriers.list.unshift(courier)
        }

        const loadDispatcherSectors = async () => {
            try {
                data_state.dispatcher_sectors.is_loading = true

                const response = await dispatcherSectorsAPI.getAllDispatcherSectors()
                data_state.dispatcher_sectors.list = response.data.data

                data_state.dispatcher_sectors.is_failed = false
            } catch (error) {
                data_state.dispatcher_sectors.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении секторов диспетчера произошла ошибка',
                })
            } finally {
                data_state.dispatcher_sectors.is_loading = false
            }
        }

        onBeforeMount(async () => {
            loadCompanies()
            loadDispatcherSectors()
            loadCouriers(1)
            await loadOneC(1)
        })

        return {
            state,
            data_state,
            resend,
            reset,
            loadOneC,
            loadCompanies,
            loadCouriers,
            searchCouriers,
            unshiftFindedCourier,
            loadDispatcherSectors,
        }
    },
})
